import React from 'react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: 'small' | 'medium' | 'large';
  hideCloseButton?: boolean;
}

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, children, size = 'medium', hideCloseButton = false }) => {
  if (!isOpen) return null;

  const getSizeClasses = () => {
    switch (size) {
      case 'small':
        return 'max-w-md';
      case 'large':
        return 'max-w-4xl';
      default:
        return 'max-w-2xl';
    }
  };

  return (
    <div className="fixed inset-0 z-[150] overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={hideCloseButton ? undefined : onClose}
        style={{ position: 'fixed', width: '100vw', height: '100vh' }}
      />

      {/* Modal */}
      <div className="fixed inset-0 flex items-center justify-center p-4">
        {/* flex-col + max-h so a panel taller than the viewport scrolls its body
            instead of overflowing off both ends (the wrapper's p-4 is the 2rem). */}
        <div className={`relative w-full ${getSizeClasses()} max-h-[calc(100vh-2rem)] flex flex-col transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 shadow-xl transition-all`}>
          {/* Header */}
          <div className="shrink-0 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">{title}</h3>
              {!hideCloseButton && (
                <button
                  type="button"
                  className="text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400"
                  onClick={onClose}
                >
                  <span className="sr-only">Close</span>
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-6 py-4 dark:text-gray-100">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Modal;
